import React, { useEffect, useState } from "react"
import { Formik, Form, Field, ErrorMessage } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Button, Card, CardBody, CardTitle, Input, Spinner } from "reactstrap"
import { addItemSchemas } from "components/Common/validation"
import Switch from "components/Shared/Switch"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { addItem } from "store/actions/itemsAction"

import {
  getCategories,
  getItemCategories,
} from "store/actions/categoriesAction"
import { showAlert } from "store/reducers/alertReducer"
import BackButton from "components/Shared/BackButton"
import {
  getImagePreviewSrc,
  handleFileChange,
} from "components/Shared/fileHandlers"
import Select from "components/Shared/TranslatedSelect"
import { getCountryList } from "store/actions/countriesAction"
import { useTranslation } from "react-i18next"
import { itemHeight, itemMaxSize, itemWidth } from "components/Common/ImageSize"
import TextError from "components/Shared/TextError"
import TranslatedFileInput from "components/Shared/TranslatedFileInput"
import { getRemoteImageUrlErrorMessage } from "utils/imageUrlValidation"
import { useBrandOptions, useShowFastMovingItemToggle } from "hooks"
import { getItemTagsList } from "store/actions/itemTagsAction"
import { getItemsListDropdown } from "store/actions/itemsAction"
import {
  appendItemExtendedFields,
  COOKING_OPTION_LABELS,
  COOKING_OPTION_VALUES,
  defaultItemExtendedFields,
  getStorageTemperatureError,
  NET_WEIGHT_UNITS,
} from "helpers/itemFormPayload"
import { ItemPreviewTabsField } from "./itemPreview"
import { multiSelectFieldProps } from "./multiSelectScrollStyles"

const initialValues = {
  nameAr: "",
  nameEn: "",
  descriptionAr: "",
  descriptionEn: "",
  displayOrder: null,
  posId: "",
  imageType: "UPLOAD",
  imageUrl: "",
  imageFile: null,
  itemPrice: null,
  originalPrice: null,
  caloriesForDisplay: "",
  personCountPerServing: "",
  caloriesForCalculations: 0,
  enabled: true,
  disablePickup: false,
  disableDelivery: false,
  isFastMoving: false,
  mostSelling: false,
  category: "",
  isDiscountAllowed: true,
  isCombo: false,
  isFeatured: false,
  specialPrice: false,
  disabledBranches: [],
  outOfStockForBranches: [],
  countryCode: null,
  weight: 0,
  width: 0,
  height: 0,
  length: 0,
  minimum: 0,
  maximum: 0,
  SKU: "",
}

const AddItem = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = ` ${
    mode === "add" || mode === "allItemsAdd" ? t("Add") : t("Edit")
  } ${t("Item ")}`

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { categoryId } = useParams()
  const { countryList } = useSelector(state => state.Countries)
  const [formValues, setFormValues] = useState(initialValues)
  const { allowedBrands, hasOnlyOneBrand } = useBrandOptions()
  const showFastMovingToggle = useShowFastMovingItemToggle()

  const { isLoading } = useSelector(state => state.Items)

  const { itemCategories } = useSelector(state => state.Categories)
  const { isImageCompressing } = useSelector(state => state.Common)
  const { itemTagsList } = useSelector(state => state.ItemTags)
  const { itemsListDropdown } = useSelector(state => state.Items)
  const [selectedTags, setSelectedTags] = useState([])
  const [extendedFields, setExtendedFields] = useState(defaultItemExtendedFields)
  const [storageTemperatureError, setStorageTemperatureError] = useState("")

  const cookingSelectOptions = COOKING_OPTION_VALUES.map(option => ({
    value: option,
    label: t(COOKING_OPTION_LABELS[option] || option),
  }))

  const relatedProductOptions = (itemsListDropdown || []).map(item => ({
    value: item._id,
    label: language === "sa" ? item.nameAr : item.name,
  }))

  const addOtherImageRow = () => {
    setExtendedFields(prev => ({
      ...prev,
      images: [
        ...(prev.images || []),
        { imageUrl: "", imageUrlType: "URL", imageFile: null },
      ],
    }))
  }

  const updateOtherImage = (index, patch) => {
    setExtendedFields(prev => {
      const images = [...(prev.images || [])]
      images[index] = { ...images[index], ...patch }
      return { ...prev, images }
    })
  }

  const validateOtherImageUrl = async (index, url) => {
    const trimmed = String(url || "").trim()
    if (!trimmed) return
    const error = await getRemoteImageUrlErrorMessage(trimmed, {
      exactWidth: itemWidth,
      exactHeight: itemHeight,
    })
    if (error) {
      dispatch(showAlert({ text: error, type: "error" }))
      updateOtherImage(index, { imageUrl: "" })
    }
  }

  const removeOtherImage = index => {
    setExtendedFields(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index),
    }))
  }

  // SET CATEGORY IF HAVE ON WINDOW URL
  useEffect(() => {
    if (categoryId) {
      setFormValues({
        ...formValues,
        category: categoryId,
      })
    }
  }, [categoryId])

  useEffect(() => {
    // getCategoriesList()
    fetchCountryList()
    dispatch(getItemTagsList())
    dispatch(getItemsListDropdown({ isCombo: false }))
  }, [])

  const fetchCountryList = () => {
    dispatch(getCountryList())
  }

  const getCategoriesList = countryCode => {
    if (!hasOnlyOneBrand) return dispatch(getItemCategories({ countryCode }))
    if (allowedBrands && allowedBrands?.length == 0)
      return dispatch(getItemCategories({ countryCode }))

    const brandId = allowedBrands && allowedBrands[0]?._id

    dispatch(getItemCategories({ countryCode, brandId }))
  }

  const handleSubmit = values => {
    const temperatureError = getStorageTemperatureError(
      extendedFields.storageTemperature,
      t
    )
    if (temperatureError) {
      setStorageTemperatureError(temperatureError)
      return
    }

    const {
      nameAr,
      nameEn,
      descriptionAr,
      descriptionEn,
      displayOrder,
      imageType,
      imageUrl,
      imageFile,
      itemPrice,
      originalPrice,
      caloriesForDisplay,
      personCountPerServing,
      caloriesForCalculations,
      enabled,
      disablePickup,
      disableDelivery,
      isFastMoving,
      mostSelling,
      category,
      countryCode,
      weight,
      width,
      height,
      length,
      minimum,
      maximum,
      SKU,
    } = values

    let data = new FormData()
    data.append("name", nameEn)
    data.append("nameAr", nameAr)
    data.append("description", descriptionEn)
    data.append("descriptionAr", descriptionAr)
    data.append("displayOrder", displayOrder)
    data.append("imageUrl", imageUrl)
    data.append("imageUrlType", imageType)
    // data.append("status", enabled ? 1 : 0)
    data.append("image", imageFile)
    data.append("personCountPerServing", personCountPerServing)
    data.append("disableForPickup", disablePickup)
    data.append("isFastMoving", showFastMovingToggle && isFastMoving === true)
    data.append("mostSelling", mostSelling === true)
    // data.append("disableForDelivery", disableDelivery)
    data.append("price", itemPrice)
    data.append("originalPrice", originalPrice)
    data.append("calories", caloriesForCalculations)
    data.append("calorieCount", caloriesForCalculations ?? 0)
    data.append("countryCode", countryCode?.value?.toUpperCase())
    data.append("weight", weight ?? 0)
    data.append("width", width)
    data.append("height", height)
    data.append("length", length)
    data.append("minimum", minimum)
    data.append("maximum", maximum)
    data.append("SKU", SKU)
    if (selectedTags?.length > 0) {
      selectedTags.forEach((tag, index) => {
        data.append(`tags[${index}]`, tag.value)
      })
    }

    appendItemExtendedFields(
      data,
      extendedFields,
      {
        primaryImage: {
          imageUrl: imageUrl || "",
          imageUrlType: imageType || "URL",
        },
      }
    )

    if (mode === "add") {
      data.append("categoryId", categoryId)
      dispatch(addItem(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            // navigate(`/menus/categories/${categoryId}/items`)
            navigate(-1)
            dispatch(showAlert({ text: msg, type: "success" }))
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "allItemsAdd") {
      data.append("categoryId", category)
      dispatch(addItem(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  return (
    <div className="page-content add_item_page">
      <div className="container-fluid">
        <Breadcrumbs />
        <Card>
          <CardBody>
            <CardTitle className="d-flex align-items-center">
              <BackButton />
              {mode === "add" || mode === "allItemsAdd"
                ? t("Add")
                : t("Edit")}{" "}
              {t("Items")}
            </CardTitle>
            <Formik
              enableReinitialize={true}
              initialValues={formValues}
              onSubmit={handleSubmit}
              validationSchema={addItemSchemas(mode)}
            >
              {({ errors, touched, values, isSubmitting, setFieldValue }) => (
                <Form>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="nameEn">
                          {" "}
                          <span className="text-danger">*</span>{" "}
                          {t("Name(English)")}
                        </label>
                        <Field
                          type="text"
                          id="nameEn"
                          name="nameEn"
                          placeholder={t("Name(English)")}
                          className={`form-control ${
                            touched.nameEn && errors.nameEn ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="nameEn" />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="nameAr">
                          <span className="text-danger">*</span>{" "}
                          {t("Name(عربي)")}
                        </label>
                        <Field
                          type="text"
                          id="nameAr"
                          style={{ direction: "rtl" }}
                          name="nameAr"
                          placeholder={t("Name(عربي)")}
                          className={`form-control ${
                            touched.nameAr && errors.nameAr ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="nameAr" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="descriptionEn">
                          {t("Description(English)")}
                        </label>
                        <Field
                          // type="text"
                          as="textarea"
                          rows={1}
                          id="descriptionEn"
                          name="descriptionEn"
                          placeholder={t("Description(English)")}
                          className={`form-control ${
                            touched.descriptionEn && errors.descriptionEn
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="descriptionEn" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="descriptionAr">
                          {t("Description(عربي)")}
                        </label>
                        <Field
                          as="textarea"
                          rows={1}
                          id="descriptionAr"
                          style={{ direction: "rtl" }}
                          name="descriptionAr"
                          placeholder={t("Description(عربي)")}
                          className={`form-control ${
                            touched.descriptionAr && errors.descriptionAr
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="descriptionAr" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="mt-2">
                        <label htmlFor="countryCode">
                          <span className="text-danger">*</span>{" "}
                          {t("Select Country")}
                        </label>
                        <Select
                          value={values.countryCode}
                          onChange={selectedValue => {
                            setFieldValue("countryCode", selectedValue)
                            getCategoriesList(selectedValue.value)
                            mode === "allItemsAdd" &&
                              setFieldValue("category", "")
                          }}
                          options={countryList?.map(item => ({
                            label: language === "sa" ? item.nameAr : item.name,
                            value: item.countryCode,
                          }))}
                          placeholder={t("Select Country")}
                          className={`select2-selection ${
                            touched.countryCode && errors.countryCode
                              ? "danger_color_border"
                              : ""
                          } `}
                        />
                        <TextError name="countryCode" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="displayOrder">
                          <span className="text-danger">*</span>
                          {t("Display Order")}
                        </label>
                        <Field
                          type="text"
                          id="displayOrder"
                          name="displayOrder"
                          placeholder={t("Display Order")}
                          className={`form-control ${
                            touched.displayOrder && errors.displayOrder
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="displayOrder" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="SKU">
                          {t("SKU")}
                        </label>
                        <Field
                          type="text"
                          id="SKU"
                          name="SKU"
                          placeholder={t("SKU")}
                          className={`form-control ${
                            touched.SKU && errors.SKU ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="SKU" />
                      </div>
                    </div>

                    {/* <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="posId">Pos ID</label>
                        <Field
                          type="text"
                          id="posId"
                          name="posId"
                          placeholder="Enter POS ID"
                          className={`form-control ${
                            touched.posId && errors.posId ? "is-invalid" : ""
                          }`}
                        />
                        <TextError
                          name="posId"
                          
                        />
                      </div>
                    </div> */}

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="itemPrice">
                          {" "}
                          <span className="text-danger">*</span>{" "}
                          {t("Item Price")}
                        </label>
                        <Field
                          type="text"
                          id="itemPrice"
                          name="itemPrice"
                          placeholder="e.g. 10"
                          className={`form-control ${
                            touched.itemPrice && errors.itemPrice
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="itemPrice" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="originalPrice">
                          {" "}
                          <span className="text-danger">*</span>{" "}
                          {t("Original Price")}
                        </label>
                        <Field
                          type="text"
                          id="originalPrice"
                          name="originalPrice"
                          placeholder="e.g. 10"
                          className={`form-control ${
                            touched.originalPrice && errors.originalPrice
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="originalPrice" />
                      </div>
                    </div>

                    {/* <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="caloriesForDisplay">
                          {t("Calories For Display")}
                        </label>
                        <Field
                          type="text"
                          id="caloriesForDisplay"
                          name="caloriesForDisplay"
                          placeholder="E.g. 150- 200"
                          className="form-control"
                        />
                      </div>
                    </div> */}

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="personCountPerServing">
                          <span className="text-danger">*</span>{" "}
                          {t("Person Count Per Serving")}
                        </label>
                        <Field
                          type="text"
                          id="personCountPerServing"
                          name="personCountPerServing"
                          placeholder="E.g. 1"
                          className={`form-control ${
                            touched.personCountPerServing &&
                            errors.personCountPerServing
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="personCountPerServing" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="caloriesForCalculations">
                          {t("Calories for Calculations")}
                        </label>
                        <Field
                          type="text"
                          id="caloriesForCalculations"
                          name="caloriesForCalculations"
                          placeholder="E.g. 200"
                          className={`form-control ${
                            touched.caloriesForCalculations &&
                            errors.caloriesForCalculations
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="caloriesForCalculations" />
                      </div>
                    </div>

                    {/* Dimension fields hidden from the add item form.
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="weight">{t("Weight")}</label>
                        <Field
                          type="text"
                          id="weight"
                          name="weight"
                          placeholder="e.g. 10"
                          className={`form-control ${
                            touched.weight && errors.weight ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="weight" />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="width">{t("Width")}</label>
                        <Field
                          type="text"
                          id="width"
                          name="width"
                          placeholder="e.g. 10"
                          className={`form-control ${
                            touched.width && errors.width ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="width" />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="height">{t("Height")}</label>
                        <Field
                          type="text"
                          id="height"
                          name="height"
                          placeholder="e.g. 10"
                          className={`form-control ${
                            touched.height && errors.height ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="height" />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="length">{t("Length")}</label>
                        <Field
                          type="text"
                          id="length"
                          name="length"
                          placeholder="e.g. 10"
                          className={`form-control ${
                            touched.length && errors.length ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="length" />
                      </div>
                    </div>
                    */}
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="minimum">
                          {" "}
                          <span className="text-danger">*</span> {t("Minimum")}
                        </label>
                        <Field
                          type="text"
                          id="minimum"
                          name="minimum"
                          placeholder="e.g. 10"
                          className={`form-control ${
                            touched.minimum && errors.minimum
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="minimum" />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="maximum">
                          {" "}
                          <span className="text-danger">*</span> {t("Maximum")}
                        </label>
                        <Field
                          type="text"
                          id="maximum"
                          name="maximum"
                          placeholder="e.g. 10"
                          className={`form-control ${
                            touched.maximum && errors.maximum
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="maximum" />
                      </div>
                    </div>

                    {/* SHOW ONLY IF ADD WITHOUT CATEGORY SELECTION */}
                    {mode === "allItemsAdd" && (
                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="category">
                            <span className="text-danger">*</span>{" "}
                            {t("Category")}
                          </label>
                          <Field
                            as="select"
                            name="category"
                            id="category"
                            readOnly
                            className={`form-control ${
                              errors.category && touched.category
                                ? "is-invalid"
                                : ""
                            }`}
                          >
                            <option hidden>{t("Select Category")}</option>

                            {itemCategories &&
                              itemCategories?.map(option => (
                                <option key={option._id} value={option._id}>
                                  {language === "sa"
                                    ? `${option.nameAr} - ${option?.menu?.appMenuId}`
                                    : `${option.name} - ${option?.menu?.appMenuId}`}
                                </option>
                              ))}
                          </Field>
                          <TextError
                            name="category"
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </div>
                    )}

                    {/* <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor=""></label>
                         allItemsAdd
                      </div>
                    </div> */}
                    {/* <div className="col-lg-2">
                      <div className="form_field">
                        <label htmlFor="enabled">Enabled</label>
                        <Switch
                          isChecked={values.enabled}
                          onClickHandler={() => {
                            setFieldValue("enabled", !values.enabled)
                          }}
                        />
                      </div>
                    </div> */}
                    <div className={mode === "allItemsAdd" ? "col-lg-6" : "col-lg-12"}>
                      <div className="form_field">
                        <label htmlFor="itemTags">{t("Tags")}</label>
                        <Select
                          inputId="itemTags"
                          value={selectedTags}
                          onChange={setSelectedTags}
                          options={itemTagsList?.map(option => ({
                            label:
                              language === "sa" ? option.nameAr : option.name,
                            value: option._id,
                            name: option.name,
                            nameAr: option.nameAr,
                            labelAr: option.nameAr,
                            displayOrder: option.displayOrder,
                          }))}
                          isMulti
                          isClearable
                          placeholder={t("Select Tags (optional)")}
                          closeMenuOnSelect={false}
                          {...multiSelectFieldProps}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="disablePickup">
                          {t("Disable for Pickup")}
                        </label>
                        <Switch
                          isChecked={values.disablePickup}
                          onClickHandler={() => {
                            setFieldValue(
                              "disablePickup",
                              !values.disablePickup
                            )
                          }}
                        />
                      </div>
                    </div>
                    {/* <div className="col-lg-2">
                      <div className="form_field">
                        <label htmlFor="isDisableDelivery">
                          Disable for Delivery
                        </label>

                        <Switch
                          isChecked={values.disableDelivery}
                          onClickHandler={() => {
                            setFieldValue(
                              "disableDelivery",
                              !values.disableDelivery
                            )
                          }}
                        />
                      </div>
                    </div> */}

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label>{t("Net weight")}</label>
                        <div className="d-flex gap-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-control"
                            value={extendedFields.netWeight?.value ?? 0}
                            onChange={e =>
                              setExtendedFields(prev => ({
                                ...prev,
                                netWeight: {
                                  ...prev.netWeight,
                                  value: e.target.value,
                                },
                              }))
                            }
                          />
                          <Input
                            type="select"
                            className="form-control"
                            value={extendedFields.netWeight?.unit || "g"}
                            onChange={e =>
                              setExtendedFields(prev => ({
                                ...prev,
                                netWeight: {
                                  ...prev.netWeight,
                                  unit: e.target.value,
                                },
                              }))
                            }
                          >
                            {NET_WEIGHT_UNITS.map(unit => (
                              <option key={unit.value} value={unit.value}>
                                {unit.label}
                              </option>
                            ))}
                          </Input>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label>{t("Storage temperature")}</label>
                        <Input
                          type="text"
                          className={`form-control ${
                            storageTemperatureError ? "is-invalid" : ""
                          }`}
                          placeholder={t("e.g. -18 Celsius")}
                          value={extendedFields.storageTemperature ?? ""}
                          onChange={e => {
                            const storageTemperature = e.target.value
                            setExtendedFields(prev => ({
                              ...prev,
                              storageTemperature,
                            }))
                            setStorageTemperatureError(
                              getStorageTemperatureError(storageTemperature, t)
                            )
                          }}
                        />
                        {storageTemperatureError ? (
                          <div className="text-danger mt-1">
                            {storageTemperatureError}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label>{t("Cooking options")}</label>
                        <Select
                          isMulti
                          options={cookingSelectOptions}
                          value={extendedFields.cookingOptions || []}
                          onChange={selected =>
                            setExtendedFields(prev => ({
                              ...prev,
                              cookingOptions: selected || [],
                            }))
                          }
                          placeholder={t("Select cooking options")}
                          closeMenuOnSelect={false}
                          {...multiSelectFieldProps}
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label>{t("Related products")}</label>
                        <Select
                          isMulti
                          options={relatedProductOptions}
                          value={extendedFields.relatedProducts || []}
                          onChange={selected =>
                            setExtendedFields(prev => ({
                              ...prev,
                              relatedProducts: selected || [],
                            }))
                          }
                          placeholder={t("Select related products")}
                          closeMenuOnSelect={false}
                          {...multiSelectFieldProps}
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="imageType">
                          <span className="text-danger">*</span>{" "}
                          {t("Image Type")}
                        </label>
                        <Field
                          as="select"
                          id="imageType"
                          name="imageType"
                          className={`form-control ${
                            touched.imageType && errors.imageType
                              ? "is-invalid"
                              : ""
                          }`}
                          onChange={e => {
                            const selectedValue = e.target.value
                            setFieldValue("imageType", selectedValue)
                            // Reset the value of imageURL and imageFile when the image type changes
                            setFieldValue("imageUrl", "")
                            setFieldValue("imageFile", null)
                          }}
                        >
                          <option value="URL">{t("Image Url")}</option>
                          <option value="UPLOAD">{t("Upload Image")}</option>
                        </Field>
                        <TextError name="imageType" />
                      </div>
                    </div>

                    <div className="w-100" />

                    <div className="col-lg-6">
                      <div className="form_field">
                        {/* IMAGE URL */}
                        {values.imageType === "URL" && (
                          <div className="form_field">
                            <label htmlFor="imageUrl">
                              <span className="text-danger">*</span> {t("URL")}
                            </label>
                            <Field
                              type="text"
                              id="imageUrl"
                              name="imageUrl"
                              className={`form-control ${
                                touched.imageUrl && errors.imageUrl
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            {touched.imageUrl && errors.imageUrl ? (
                              <TextError name="imageUrl" />
                            ) : (
                              <p className="pt-2 text-muted">
                                {t(
                                  "Recommended dimensions: {{itemWidth}}x{{itemHeight}} pixels",
                                  {
                                    itemWidth: itemWidth,
                                    itemHeight: itemHeight,
                                  }
                                )}
                              </p>
                            )}
                          </div>
                        )}
                        {/* UPLOAD IMAGE */}
                        {values.imageType === "UPLOAD" && (
                          <div className="form_field position-relative">
                            <label htmlFor="imageFile">
                              {" "}
                              <span className="text-danger">*</span>{" "}
                              {t("Upload Image")}
                            </label>
                            <TranslatedFileInput
                              id="imageFile"
                              name="imageFile"
                              invalid={
                                !!(touched.imageFile && errors.imageFile)
                              }
                              accept="image/jpeg, image/jpg, image/png, image/webp"
                              onChange={e => {
                                handleFileChange(
                                  e,
                                  "imageFile",
                                  setFieldValue,
                                  itemWidth,
                                  itemHeight,
                                  itemMaxSize
                                )
                              }}
                            />
                            <p className="pt-2">
                              {t(
                                "Recommended dimensions: Square Image and Size should be less than or equal to {{size}} KB",
                                {
                                  width: itemWidth,
                                  height: itemHeight,
                                  size: itemMaxSize,
                                }
                              )}
                            </p>
                            {isImageCompressing && (
                              <Spinner
                                color="primary"
                                className="file_upload_btn"
                              />
                            )}
                          </div>
                        )}
                        <TextError name="imageFile" />
                      </div>
                      {(() => {
                        const updatedMode =
                          mode === "add" || mode === "allItemsAdd"
                            ? "add"
                            : mode
                        return (
                          getImagePreviewSrc(updatedMode, values) && (
                            <div className="mt-3">
                              <img
                                src={getImagePreviewSrc(updatedMode, values)}
                                alt="Preview"
                                className="img_preview"
                              />
                            </div>
                          )
                        )
                      })()}
                    </div>

                    <ItemPreviewTabsField
                      values={values}
                      selectedTags={selectedTags}
                      mode={mode}
                    />

                    {/* <div className="col-12">
                      <span>Recommended size: 512 x 512</span>
                    </div> */}

                    <div className="col-12">
                      <div className="form_field">
                        <label>{t("Other images")}</label>
                        <p className="text-muted small mb-2">{t("Optional")}</p>
                        {(extendedFields.images || []).map((img, index) => (
                          <div
                            key={`other-image-${index}`}
                            className="border rounded p-3 mb-3"
                          >
                            <div className="row">
                              <div className="col-lg-6">
                                <div className="form_field">
                                  <label>{t("Image Type")}</label>
                                  <Input
                                    type="select"
                                    value={img.imageUrlType || "URL"}
                                    onChange={e => {
                                      updateOtherImage(index, {
                                        imageUrlType: e.target.value,
                                        imageUrl: "",
                                        imageFile: null,
                                      })
                                    }}
                                  >
                                    <option value="URL">{t("Image Url")}</option>
                                    <option value="UPLOAD">
                                      {t("Upload Image")}
                                    </option>
                                  </Input>
                                </div>
                              </div>
                              <div className="col-lg-6">
                                {img.imageUrlType === "URL" ? (
                                  <div className="form_field">
                                    <label>{t("URL")}</label>
                                    <Input
                                      type="text"
                                      placeholder={t("Image URL")}
                                      value={img.imageUrl || ""}
                                      onChange={e =>
                                        updateOtherImage(index, {
                                          imageUrl: e.target.value,
                                        })
                                      }
                                      onBlur={e =>
                                        validateOtherImageUrl(
                                          index,
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                ) : (
                                  <div className="form_field position-relative">
                                    <label>{t("Upload Image")}</label>
                                    <TranslatedFileInput
                                      accept="image/jpeg, image/jpg, image/png, image/webp"
                                      onChange={e => {
                                        handleFileChange(
                                          e,
                                          `otherImageFile_${index}`,
                                          (_field, file) => {
                                            updateOtherImage(index, {
                                              imageFile: file,
                                              imageUrl: "",
                                            })
                                          },
                                          itemWidth,
                                          itemHeight,
                                          itemMaxSize
                                        )
                                      }}
                                    />
                                    {isImageCompressing && (
                                      <Spinner
                                        color="primary"
                                        className="file_upload_btn"
                                      />
                                    )}
                                    {img.imageUrl && !img.imageFile && (
                                      <p className="text-muted small mt-1 mb-0">
                                        {t(
                                          "Current image will be kept if you do not upload a new file."
                                        )}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            {(img.imageUrl || img.imageFile) && (
                              <div className="mt-2">
                                <img
                                  src={
                                    img.imageFile
                                      ? URL.createObjectURL(img.imageFile)
                                      : img.imageUrl
                                  }
                                  alt=""
                                  className="img_preview"
                                />
                              </div>
                            )}
                            <Button
                              color="danger"
                              outline
                              type="button"
                              size="sm"
                              className="mt-2"
                              onClick={() => removeOtherImage(index)}
                            >
                              {t("Remove")}
                            </Button>
                          </div>
                        ))}
                        <Button
                          color="secondary"
                          outline
                          type="button"
                          size="sm"
                          onClick={addOtherImageRow}
                        >
                          {t("Add image")}
                        </Button>
                      </div>
                    </div>

                    {showFastMovingToggle ? (
                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="isFastMoving">
                            {t("Fast-moving item")}
                          </label>
                          <Switch
                            isChecked={values.isFastMoving}
                            onClickHandler={() => {
                              setFieldValue(
                                "isFastMoving",
                                !values.isFastMoving
                              )
                            }}
                          />
                          <small className="text-muted d-block mt-2">
                            {t(
                              "When all cart items are fast-moving, same-day evening delivery may be offered before the tenant cut-off time."
                            )}
                          </small>
                        </div>
                      </div>
                    ) : null}

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="mostSelling">
                          {t("Most Selling")}
                        </label>
                        <Switch
                          isChecked={values.mostSelling}
                          onClickHandler={() => {
                            setFieldValue("mostSelling", !values.mostSelling)
                          }}
                        />
                      </div>
                    </div>

                    <div className="col-12 d-flex justify-content-end my-4">
                      <Button
                        type="button"
                        className="mx-2"
                        color="secondary"
                        onClick={() => navigate(-1)}
                      >
                        {t("Cancel")}
                      </Button>

                      <Button
                        type="submit"
                        color="success"
                        disabled={isLoading || isImageCompressing}
                        className="w_fit_content"
                      >
                        {mode === "add" || mode === "allItemsAdd"
                          ? t("Add")
                          : t("Save")}
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default AddItem
